import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Bucket } from '@mayksel/types';
import type { PaginatedResponse } from '@mayksel/types';
import type { CreateBucketInput, UpdateBucketInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export interface BucketFilters {
  search?: string;
  companyId?: string;
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useBuckets(filters: BucketFilters = {}) {
  const { getToken, workspaceId } = useApiOpts();
  const { search, companyId, status, page = 1, limit = 50, sortBy = 'created_at', sortDir = 'desc' } = filters;

  return useQuery<PaginatedResponse<Bucket>>({
    queryKey: ['buckets', workspaceId, search, companyId, status, page, limit, sortBy, sortDir],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (companyId) params.set('companyId', companyId);
      if (status) params.set('status', status);
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);

      const opts: { token?: string | undefined; workspaceId?: string | undefined } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<PaginatedResponse<Bucket>>(
        `/v1/buckets?${params.toString()}`,
        opts,
      );
    },
    enabled: !!workspaceId,
  });
}

export function useBucket(id: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Bucket>({
    queryKey: ['buckets', workspaceId, id],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Bucket>(`/v1/buckets/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!id,
  });
}

export function useCreateBucket() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBucketInput) => {
      const token = await getToken();
      return api.post<Bucket>('/v1/buckets', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['buckets', workspaceId] });
    },
  });
}

export function useUpdateBucket(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBucketInput) => {
      const token = await getToken();
      return api.patch<Bucket>(`/v1/buckets/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['buckets', workspaceId] });
    },
  });
}

export function useDeleteBucket() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/buckets/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['buckets', workspaceId] });
    },
  });
}
